/**
 * Tiny framework-agnostic toast store.
 *
 * Lives outside React so non-component code (the axios response interceptor in
 * api/client.ts) can surface errors. A <ToastContainer> subscribes and renders.
 */
export type ToastType = "error" | "info" | "success";

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
let listeners: Listener[] = [];
let nextId = 1;

// Suppress identical messages fired in a burst (e.g. several dashboard fetches
// failing at once on a server outage) so the user sees one toast, not ten.
const DEDUPE_MS = 3000;
const recent = new Map<string, number>();

function emit() {
  for (const l of listeners) l(toasts);
}

export function subscribe(listener: Listener): () => void {
  listeners.push(listener);
  listener(toasts); // sync current state immediately
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function pushToast(message: string, type: ToastType = "error", ttlMs = 6000): void {
  const now = Date.now();
  // prune stale dedupe entries and drop bursts of the same message
  for (const [msg, ts] of recent) if (now - ts > DEDUPE_MS) recent.delete(msg);
  const last = recent.get(message);
  if (last !== undefined && now - last < DEDUPE_MS) return;
  recent.set(message, now);

  const id = nextId++;
  toasts = [...toasts, { id, message, type }];
  emit();
  if (ttlMs > 0) setTimeout(() => dismissToast(id), ttlMs);
}

export function dismissToast(id: number): void {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

// Test-only reset hook.
export function _resetToasts(): void {
  toasts = [];
  listeners = [];
  recent.clear();
  nextId = 1;
}
